/**
 * Acceptance expiry for Speed-to-Lead outbound tasks.
 *
 * DESIGN: Two-layer expiry strategy
 * ----------------------------------
 * Layer 1 — Frontend timer (this file): starts immediately after a successful
 * claimTask response. Fires releaseTask after 60 seconds if the CSR has not
 * dialled. Cancelled by cancelAcceptanceExpiry() when the CSR clicks Dial.
 *
 * Layer 2 — BFF cron job (task 4b): queries Task Hub across all tenants for
 * tasks with sys.status=to_do, sys.assigned_to!=null, and assignment timestamp
 * older than 10 minutes. Releases them server-side. This is the safety net for
 * any scenario where the frontend timer cannot fire.
 *
 * PAGE REFRESH / SIGN-OUT BEHAVIOUR
 * ----------------------------------
 * The timer lives only in JS memory. On page refresh or sign-out, the timer is
 * destroyed but TasksModule rehydrates tasks from local storage. This would leave
 * the CSR looking at a pre-dial task with no countdown running.
 *
 * To prevent this, SpeedToLeadTasksModule.preload() deletes any pre-dial STL
 * task found in local state on startup (hasMadeCall === false). The task stays
 * assigned in the backend until the BFF cron job releases it (up to 10 minutes),
 * after which the CSR can reclaim it. No releaseTask call is made on restore —
 * backend cleanup is the cron's responsibility.
 *
 * Post-dial tasks (hasMadeCall === true) are NOT deleted on restore — they are
 * handled separately by the re-login restoration flow (task 5b).
 */
import appManager from '@app/appManager';
import TasksModule from '@modules/TasksModule';
import InboxModule from '@modules/InboxModule';
import ToastModule from '@modules/ToastModule';
import { releaseTask } from '@modules/SpeedToLeadTasksModule/mutations/releaseTask';

const logger = appManager.logger('SpeedToLead');
export const STL_ACCEPTANCE_EXPIRY_DURATION_MS = 60_000;

let expiryTimer: ReturnType<typeof setTimeout> | null = null;
let pendingTaskId: string | null = null;
let pendingTenantId: string | null = null;

const handleExpiry = async (): Promise<void> => {
  const taskId = pendingTaskId;
  const tenantId = pendingTenantId;

  // Clear references before async work so a rapid re-claim doesn't double-fire
  expiryTimer = null;
  pendingTaskId = null;
  pendingTenantId = null;

  if (!taskId || !tenantId) return;

  logger.info('stl:expiry:fired', { taskId, tenantId });

  try {
    const released = await releaseTask({ taskId, tenantId });
    if (released) {
      logger.info('stl:expiry:released', { taskId });
    } else {
      logger.warn('stl:expiry:release-failed-returned-false', { taskId });
    }
  } catch (err) {
    // Spec: on success OR failure, proceed with local cleanup — do not surface the error
    logger.error('stl:expiry:release-failed', { taskId, err });
  }

  TasksModule.deleteTask(taskId);
  InboxModule.setSelectedTaskId(undefined);
  logger.info('stl:expiry:local-cleanup-done', { taskId });

  ToastModule.showToast({
    title: 'This task expired and has been returned to the task list',
    status: 'info',
  });
};

/**
 * Starts the 60-second acceptance expiry countdown for a claimed task.
 * If a countdown is already running (e.g. from a previous claim), it is
 * cancelled first as a safety net.
 */
export const startAcceptanceExpiry = (
  tenantId: string,
  taskId: string,
): void => {
  cancelAcceptanceExpiry();

  pendingTaskId = taskId;
  pendingTenantId = tenantId;
  TasksModule.setTaskAttribute(taskId, 'expiryStartedAt', Date.now());
  expiryTimer = setTimeout(
    () => void handleExpiry(),
    STL_ACCEPTANCE_EXPIRY_DURATION_MS,
  );
  logger.info('stl:expiry:started', {
    taskId,
    tenantId,
    durationMs: STL_ACCEPTANCE_EXPIRY_DURATION_MS,
  });
};

/**
 * Cancels the active acceptance expiry countdown and returns the task that was
 * pending, or null if no countdown was running. Safe to call at any time.
 *
 * Callers that only need to cancel (e.g. CSR clicks Dial) can ignore the
 * return value. Callers that need to act on the cancelled task (e.g.
 * handleStlPreDialRelease) use the return value to avoid a separate read step.
 */
export const cancelAcceptanceExpiry = (): {
  taskId: string;
  tenantId: string;
} | null => {
  const taskId = pendingTaskId;
  const tenantId = pendingTenantId;

  if (expiryTimer !== null) {
    clearTimeout(expiryTimer);
    expiryTimer = null;
    logger.info('stl:expiry:cancelled', { taskId });
  }
  pendingTaskId = null;
  pendingTenantId = null;

  if (taskId && tenantId) {
    return { taskId, tenantId };
  }
  return null;
};
